import { useState, type SubmitEvent } from "react";
import { contactMessageSchema } from "@shared/contracts";
import { ApiError } from "../../platform/api/api-error";
import { sendMessage } from "./contact-api";

type FieldName = "name" | "email" | "message";
export type FieldErrors = Partial<Record<FieldName, string>>;

export type ContactFormState =
  | { status: "editing"; fieldErrors: FieldErrors; formError: string | null }
  | { status: "sending" }
  | { status: "sent" };

const FIELD_LABELS: Record<FieldName, string> = {
  name: "Enter your name.",
  // Short enough to share the label's row on a phone, so an error never costs the form height.
  email: "Enter an email Orla can reply to.",
  message: "At least ten characters.",
};

/**
 * The contact form's behaviour. Success is shown only after the API has confirmed that the
 * site's durable queue accepted the message; every failure is put into words.
 */
export function useContactForm(listingSlug?: string) {
  const [state, setState] = useState<ContactFormState>({
    status: "editing",
    fieldErrors: {},
    formError: null,
  });
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [challengeRound, setChallengeRound] = useState(0);

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (field: string) => {
      const value = form.get(field);
      return typeof value === "string" ? value : "";
    };
    const candidate = {
      name: text("name"),
      email: text("email"),
      message: text("message"),
      turnstileToken: challengeToken ?? "",
      ...(listingSlug ? { listingSlug } : {}),
    };
    const parsed = contactMessageSchema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      let formError: string | null = null;
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "name" || field === "email" || field === "message") {
          fieldErrors[field] ??= FIELD_LABELS[field];
        } else if (field === "turnstileToken") {
          formError = "Please wait for the spam check to finish, then send again.";
        }
      }
      setState({ status: "editing", fieldErrors, formError });
      return;
    }
    setState({ status: "sending" });
    sendMessage(parsed.data).then(
      () => {
        setState({ status: "sent" });
      },
      (error: unknown) => {
        const failure = error instanceof ApiError ? error : null;
        const fieldErrors: FieldErrors = {};
        for (const field of ["name", "email", "message"] as const) {
          if (failure?.fields[field]) fieldErrors[field] = FIELD_LABELS[field];
        }
        // A Turnstile token can be spent once, so every failed send needs a fresh one.
        setChallengeToken(null);
        setChallengeRound((round) => round + 1);
        setState({
          status: "editing",
          fieldErrors,
          formError: failure?.message ?? "The message could not be sent. Please try again.",
        });
      },
    );
  }

  return { state, submit, challengeRound, setChallengeToken };
}
